import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Workflow, Users, Building2, Plug } from 'lucide-react';
import { IconAdjustmentsAlt, IconPointerCollaboration2 } from '@tabler/icons-react';
import CustomersAffiliateIcon from '../../components/icons/IconAffiliate';
import { hasGoAppLicense } from '../../utils/userFinanceiroAccess';
import ConfigHubSearch from './ConfigHubSearch';
import {
  CONFIG_ROUTE_HOME,
  buildConfigHubCatalog,
  resolveConfigBreadcrumbModule,
} from './configHubCatalog';
import { navigateConfig } from './configNavMotion';
import './ConfigSectionTopBar.css';

const SECTION_META = {
  process: {
    title: 'Processos',
    Icon: Workflow,
    iconKind: 'lucide',
  },
  go: {
    title: 'Go',
    Icon: IconPointerCollaboration2,
    iconKind: 'tabler',
  },
  usersCustomers: {
    title: 'Customers',
    Icon: CustomersAffiliateIcon,
    iconKind: 'custom',
  },
  integrations: {
    title: 'Integrações',
    Icon: Plug,
    iconKind: 'lucide',
  },
  users: {
    title: 'Usuários',
    Icon: Users,
    iconKind: 'lucide',
  },
  company: {
    title: 'Empresa',
    Icon: Building2,
    iconKind: 'lucide',
  },
  painelControle: {
    title: 'Painel de controle',
    Icon: IconAdjustmentsAlt,
    iconKind: 'tabler',
  },
};

const SECTION_ROOT_PATH = {
  process: '/configuracoes/processos',
  go: '/configuracoes/go',
  usersCustomers: '/configuracoes/customers',
  integrations: '/configuracoes/integracoes',
  users: '/configuracoes/usuarios',
  company: '/configuracoes/empresa',
  painelControle: '/configuracoes/painel-controle',
};

function TipPortal({ anchorRect, text }) {
  if (!anchorRect || !text) return null;
  return createPortal(
    <span
      className="config-section-topbar-tip"
      style={{
        top: anchorRect.bottom + 8,
        left: anchorRect.left + anchorRect.width / 2,
      }}
      role="tooltip"
    >
      {text}
    </span>,
    document.body
  );
}

function useTip(text) {
  const anchorRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const show = useCallback(() => {
    if (!text || !anchorRef.current) return;
    setAnchorRect(anchorRef.current.getBoundingClientRect());
  }, [text]);
  const hide = useCallback(() => setAnchorRect(null), []);
  return { anchorRef, anchorRect, show, hide };
}

function SectionIcon({ meta }) {
  if (!meta?.Icon) return null;
  const { Icon, iconKind } = meta;
  if (iconKind === 'tabler' || iconKind === 'custom') {
    return <Icon size={16} stroke={1.75} aria-hidden />;
  }
  return <Icon size={16} strokeWidth={1.75} aria-hidden />;
}

function CrumbSep() {
  return (
    <ChevronRight
      className="config-section-topbar-crumb-sep"
      size={14}
      strokeWidth={2}
      aria-hidden
    />
  );
}

/**
 * Faixa full-width: voltar + breadcrumb + busca central (catálogo do hub).
 */
export default function ConfigSectionTopBar({
  sectionId,
  hubPath = CONFIG_ROUTE_HOME,
  currentUser = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const meta = SECTION_META[sectionId] || null;
  const backTip = 'Voltar às configurações';
  const back = useTip(backTip);

  const hasCustomers = Number(currentUser?.verve_customers_module) === 1;
  const hasGo = hasGoAppLicense(currentUser);
  const showStorage = hasCustomers || hasGo;

  const catalog = useMemo(
    () => buildConfigHubCatalog({ hasCustomers, hasGo, showStorage }),
    [hasCustomers, hasGo, showStorage]
  );

  const moduleLabel = useMemo(
    () =>
      resolveConfigBreadcrumbModule({
        catalog,
        pathname: location.pathname,
        search: location.search,
      }),
    [catalog, location.pathname, location.search]
  );

  if (!meta) return null;

  const sectionRoot = SECTION_ROOT_PATH[sectionId] || hubPath;

  return (
    <div className="config-section-topbar" role="banner" aria-label={`${meta.title} — configurações`}>
      <div className="config-section-topbar-inner">
        <div className="config-section-topbar-left">
          <button
            ref={back.anchorRef}
            type="button"
            className="config-section-topbar-back"
            onClick={() => navigateConfig(navigate, hubPath, 'back')}
            aria-label={backTip}
            onMouseEnter={back.show}
            onMouseLeave={back.hide}
            onFocus={back.show}
            onBlur={back.hide}
          >
            <ChevronLeft size={18} strokeWidth={2} aria-hidden />
          </button>
          <TipPortal anchorRect={back.anchorRect} text={backTip} />

          <nav className="config-section-topbar-crumbs" aria-label="Breadcrumb">
            <button
              type="button"
              className="config-section-topbar-crumb"
              onClick={() => navigateConfig(navigate, hubPath, 'back')}
            >
              Configurações
            </button>
            <CrumbSep />
            <button
              type="button"
              className={`config-section-topbar-crumb${moduleLabel ? '' : ' is-current'}`}
              onClick={() => navigate(sectionRoot)}
              aria-current={moduleLabel ? undefined : 'page'}
            >
              <span className="config-section-topbar-crumb-ico" aria-hidden>
                <SectionIcon meta={meta} />
              </span>
              {meta.title}
            </button>
            {moduleLabel ? (
              <>
                <CrumbSep />
                <span className="config-section-topbar-crumb is-current" aria-current="page">
                  {moduleLabel}
                </span>
              </>
            ) : null}
          </nav>
        </div>

        <div className="config-section-topbar-search">
          <ConfigHubSearch
            currentUser={currentUser}
            onNavigate={(path) => navigateConfig(navigate, path, 'forward')}
            showStorage={showStorage}
            configSection={sectionId}
          />
        </div>

        <div className="config-section-topbar-right" aria-hidden />
      </div>
    </div>
  );
}

export { SECTION_META };
